import styled from '@emotion/styled';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUpRightFromSquare,
  faArrowLeftFromLine,
  faArrowRightFromLine,
} from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import {
  Button,
  ConfigProvider,
  Drawer,
  Flex,
  Typography,
} from '@robinpowered/ui-kit';
import Close from '@robinpowered/ui-kit-icons/Close';
import { useFeatureFlags } from '@robinpowered/dashboard-apps-common';
import { useAuthContext } from 'contexts';
import { ProfileDetailsCardContainer } from './ProfileDetailsCard';
import { BookingsListContainer } from './BookingsList';
import { PermissionsCardContainer } from './PermissionsCard';
import { DesksPanel } from './DesksPanel';
import { ProfilePageUserProvider } from './ProfilePageUserContext';
import { ProfileSidebarPopupProvider } from './ProfileSidebarPopupContext';
import { SHELL_OVERLAY_Z_INDEX } from 'constants/zIndex';
import {
  NAV_COLLAPSED_CHANGE_EVENT,
  NAV_SIDEBAR_COLLAPSED_WIDTH,
  NAV_SIDEBAR_EXPANDED_WIDTH,
  readStoredNavCollapsed,
} from 'utils/navSidebar';
import { config } from 'config';
import { shellOverlayTheme } from './styles';

const SIDEBAR_DEFAULT_WIDTH = 340;

/**
 * Reactively tracks whether the global nav sidebar is collapsed, so the
 * fullscreen profile can size itself against the nav's footprint. The nav owns
 * the state (persisted to sessionStorage); this mirrors it on same-tab (custom
 * event) and cross-tab (storage) changes.
 */
const useNavSidebarCollapsed = (): boolean => {
  const [collapsed, setCollapsed] = useState(
    () => readStoredNavCollapsed() ?? false
  );

  useEffect(() => {
    const sync = () => setCollapsed(readStoredNavCollapsed() ?? false);
    window.addEventListener(NAV_COLLAPSED_CHANGE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(NAV_COLLAPSED_CHANGE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  return collapsed;
};

type PeopleProfileSidebarProps = {
  onClose: () => void;
  userId?: string;
  /**
   * The global search overlay is open on top of the profile. While it is, the
   * profile must not auto-dismiss — otherwise ⌘K (or a click on the search
   * mask) would close the profile out from under the search.
   */
  isSearchOpen?: boolean;
};

export const PeopleProfileSidebar = ({
  onClose,
  userId,
  isSearchOpen = false,
}: PeopleProfileSidebarProps): JSX.Element => {
  const { t } = useTranslation('peopleProfileSidebar');
  const { orgSlug, currentUser, userPermissions } = useAuthContext();

  // Viewing other people's profiles requires organization:list_users — the
  // same permission the backend uses to keep user results out of global
  // search. Enforced here rather than at the entry point so any future way
  // of opening the sidebar inherits the gate. Your own profile is always
  // viewable, matching the API's self-scoped exemption. While permissions
  // are still loading the drawer stays closed instead of flashing data.
  const isCurrentPerson = userId !== undefined && currentUser?.id === userId;
  const canViewProfile =
    isCurrentPerson ||
    (!userPermissions?.isLoading && !!userPermissions?.canListUsers);
  const permittedUserId = canViewProfile ? userId : undefined;
  const open = permittedUserId !== undefined;

  const lastUserId = useRef<string>();
  useEffect(() => {
    if (permittedUserId !== undefined) {
      lastUserId.current = permittedUserId;
    }
  }, [permittedUserId]);
  const displayedUserId = permittedUserId ?? lastUserId.current;

  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  // Focus target on open: the primary action (expand toggle or open-in-new-tab
  // button, whichever the flag renders — only one is mounted at a time).
  const primaryActionRef = useRef<HTMLButtonElement>(null);

  const [hasOpened, setHasOpened] = useState(false);
  const [hasOpenProfileModal, setHasOpenProfileModal] = useState(false);

  // Desk-card popups (overflow menus, unassign confirm) portal to
  // document.body so they can extend past the drawer's width. They report
  // open/close in balanced pairs; while any are open, outside mousedowns
  // close the popup (antd's own behavior), not the sidebar.
  const [openPopupCount, setOpenPopupCount] = useState(0);
  const handlePopupOpenChange = useCallback((popupOpen: boolean) => {
    setOpenPopupCount((count) => Math.max(0, count + (popupOpen ? 1 : -1)));
  }, []);
  const hasOpenPopup = openPopupCount > 0;
  const [isFullscreen, setIsFullscreen] = useState(false);
  const isNavCollapsed = useNavSidebarCollapsed();

  // Gates the fullscreen profile page. When off, the header shows the legacy
  // "open profile page in a new tab" button instead of the expand toggle.
  const { checkFlag } = useFeatureFlags();
  const isFullPageEnabled = checkFlag(
    'foundation(profile-sidebar-full-page)',
    false
  );
  // Only render the fullscreen layout when both the flag is on and the user has
  // expanded — so flipping the flag off can never strand the drawer fullscreen.
  const showFullscreen = isFullPageEnabled && isFullscreen;

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  // In fullscreen, stretch up to the nav sidebar's trailing edge so no
  // underlying app content peeks through beside the drawer.
  const navWidth = isNavCollapsed
    ? NAV_SIDEBAR_COLLAPSED_WIDTH
    : NAV_SIDEBAR_EXPANDED_WIDTH;
  const fullscreenWidth = `calc(100vw - ${navWidth}px)`;

  const handleAfterOpenChange = useCallback((opened: boolean) => {
    setHasOpened(opened);
    if (opened) {
      (primaryActionRef.current ?? closeButtonRef.current)?.focus();
    }
  }, []);
  const closeSidebar = useCallback(() => {
    setHasOpenProfileModal(false);
    setIsFullscreen(false);
    onClose();
  }, [onClose]);
  const profilePageUrl =
    orgSlug && displayedUserId
      ? `${config.dashboardUrl}/${orgSlug}/people/${displayedUserId}`
      : undefined;

  // A denied open request also resets the shell's selected-user state, so a
  // stale selection isn't left behind the never-opened drawer.
  useEffect(() => {
    if (
      userId !== undefined &&
      !userPermissions?.isLoading &&
      !canViewProfile
    ) {
      closeSidebar();
    }
  }, [userId, userPermissions?.isLoading, canViewProfile, closeSidebar]);

  useEffect(() => {
    if (!hasOpened) return;

    // Dismiss on mousedown (not click): the search overlay is still open at
    // this point, so `isSearchOpen` is accurate and a single check suffices.
    // Deciding on the later `click` would be too late — selecting a search
    // result closes the search first, so the click can't tell it came from
    // search. Deciding on press also matches how popovers/drawers dismiss.
    const handleMouseDown = (event: MouseEvent) => {
      if (hasOpenProfileModal || hasOpenPopup || isSearchOpen) {
        return;
      }
      const target = event.target as Node;
      if (!panelRef.current?.contains(target)) {
        closeSidebar();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== 'Escape' ||
        hasOpenProfileModal ||
        hasOpenPopup ||
        isSearchOpen
      ) {
        return;
      }
      closeSidebar();
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    closeSidebar,
    hasOpened,
    hasOpenProfileModal,
    hasOpenPopup,
    isSearchOpen,
  ]);

  return (
    <Drawer
      afterOpenChange={handleAfterOpenChange}
      panelRef={panelRef}
      aria-label={t('title')}
      closable={false}
      extra={
        <Flex align="center" gap={4}>
          {isFullPageEnabled ? (
            <Button
              aria-label={t(
                isFullscreen ? 'exitFullscreenLabel' : 'enterFullscreenLabel'
              )}
              aria-pressed={isFullscreen}
              icon={
                <FontAwesomeIcon
                  icon={
                    isFullscreen ? faArrowRightFromLine : faArrowLeftFromLine
                  }
                />
              }
              onClick={toggleFullscreen}
              ref={primaryActionRef}
              type="text"
            />
          ) : (
            profilePageUrl && (
              <Button
                aria-label={t('openProfilePageLabel')}
                href={profilePageUrl}
                icon={<FontAwesomeIcon icon={faUpRightFromSquare} />}
                ref={primaryActionRef}
                rel="noopener noreferrer"
                target="_blank"
                type="text"
              />
            )
          )}
          <Button
            ref={closeButtonRef}
            aria-label={t('closeButtonLabel')}
            icon={<Close />}
            onClick={closeSidebar}
            type="text"
          />
        </Flex>
      }
      keyboard={false}
      mask={false}
      onClose={closeSidebar}
      open={open}
      placement="right"
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          {t('title')}
        </Typography.Title>
      }
      width={showFullscreen ? fullscreenWidth : SIDEBAR_DEFAULT_WIDTH}
      zIndex={SHELL_OVERLAY_Z_INDEX}
      styles={{
        body: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <ConfigProvider theme={shellOverlayTheme}>
        <ProfileSidebarPopupProvider onPopupOpenChange={handlePopupOpenChange}>
          {displayedUserId &&
            (showFullscreen ? (
              <ProfilePageUserProvider
                key={displayedUserId}
                userId={displayedUserId}
              >
                <FullscreenBody>
                  <ProfileColumn vertical>
                    <ProfileDetailsCardContainer
                      userId={displayedUserId}
                      expanded
                    />
                    <PermissionsCardContainer userId={displayedUserId} />
                  </ProfileColumn>
                  <DesksColumn>
                    <DesksPanel
                      userId={displayedUserId}
                      isProfileSidebarOpen={open}
                      onNavigateAway={closeSidebar}
                      onModalOpenChange={setHasOpenProfileModal}
                    />
                  </DesksColumn>
                </FullscreenBody>
              </ProfilePageUserProvider>
            ) : (
              <SidebarBody vertical>
                <ProfileDetailsCardContainer userId={displayedUserId} />
                <BookingsListContainer
                  isProfileSidebarOpen={open}
                  key={displayedUserId}
                  userId={displayedUserId}
                  onNavigateAway={closeSidebar}
                  onModalOpenChange={setHasOpenProfileModal}
                />
              </SidebarBody>
            ))}
        </ProfileSidebarPopupProvider>
      </ConfigProvider>
    </Drawer>
  );
};

const SidebarBody = styled(Flex)`
  min-height: 100%;

  &::after {
    content: '';
    flex: 0 0 12px;
  }
`;

const FullscreenBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  /* No explicit gap: the profile cards' 16px right margin and the desks
     content's 16px left padding already form a 32px gutter, matching the
     FOCO-149 design. An extra gap here doubled it. */
  min-height: 0;
  overflow: hidden;
`;

const PROFILE_COLUMN_WIDTH = 360;

const ProfileColumn = styled(Flex)`
  flex: 0 0 ${PROFILE_COLUMN_WIDTH}px;
  width: ${PROFILE_COLUMN_WIDTH}px;
  overflow-y: auto;

  &::after {
    content: '';
    flex: 0 0 12px;
  }
`;

const DesksColumn = styled.div`
  flex: 1;
  min-width: 0;
  overflow-y: auto;
  padding-bottom: 12px;
`;
